import React, { ChangeEvent, KeyboardEvent, ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { Alert, Button, Input } from '../../../design-system';
import {
  faInfoCircle,
  faExclamationTriangle
} from '@fortawesome/free-solid-svg-icons';
import useUser from '../../../hooks/useUser';
import { getErrorMessage } from '../../../utils/errorMessage';
import AccountSettingsLoadingState from '../AccountSettingsLoadingState';
import {
  getAccountSettingsUserCacheKey,
  makeAccountSettingsCacheKey,
  readAccountSettingsCache,
  readThroughAccountSettingsCache,
  writeAccountSettingsCache,
} from '../accountSettingsDataCache';
import './NotificationSettings.css';

type NumericSetting = number | '';
type NotificationSettingKey = keyof NotificationSettingsState;

interface NotificationSettingsState {
  enablePerformanceAlerts: boolean;
  performanceAlertThreshold: NumericSetting;
  enableDrawdownAlerts: boolean;
  drawdownAlertThreshold: NumericSetting;
  enableBotStalenessAlerts: boolean;
  enableBotErrorAlerts: boolean;
  enableKillSwitch: boolean;
  killSwitchDrawdownThreshold: NumericSetting;
  killSwitchLiquidateAll: boolean;
}

interface NumberFieldProps {
  id: string;
  label: string;
  icon: IconDefinition;
  value: NumericSetting;
  onChange: (event: ChangeEvent<HTMLInputElement>) => void;
  onBlur?: () => void;
  onEnter?: () => void;
  min: string;
  max: string;
  placeholder: string;
}

interface SwitchRowProps {
  id: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  label: ReactNode;
  children?: ReactNode;
}

interface NotificationGroupProps {
  title: string;
  children: ReactNode;
}

interface NotificationSettingsResponse extends Partial<NotificationSettingsState> {
  message?: string;
}

const AUTOSAVE_DEBOUNCE_MS = 500;

const defaultNotificationSettings: NotificationSettingsState = {
  enablePerformanceAlerts: true,
  performanceAlertThreshold: 10.0,
  enableDrawdownAlerts: true,
  drawdownAlertThreshold: 25.0,
  enableBotStalenessAlerts: false,
  enableBotErrorAlerts: false,
  enableKillSwitch: false,
  killSwitchDrawdownThreshold: 40.0,
  killSwitchLiquidateAll: false
};

const parseSettingNumber = (value: NumericSetting): number => (
  value === '' ? Number.NaN : Number(value)
);

const settingsEqual = (left: NotificationSettingsState, right: NotificationSettingsState): boolean => (
  left.enablePerformanceAlerts === right.enablePerformanceAlerts
  && left.performanceAlertThreshold === right.performanceAlertThreshold
  && left.enableDrawdownAlerts === right.enableDrawdownAlerts
  && left.drawdownAlertThreshold === right.drawdownAlertThreshold
  && left.enableBotStalenessAlerts === right.enableBotStalenessAlerts
  && left.enableBotErrorAlerts === right.enableBotErrorAlerts
  && left.enableKillSwitch === right.enableKillSwitch
  && left.killSwitchDrawdownThreshold === right.killSwitchDrawdownThreshold
  && left.killSwitchLiquidateAll === right.killSwitchLiquidateAll
);

const NumberField = ({ id, label, icon, value, onChange, onBlur, onEnter, min, max, placeholder }: NumberFieldProps) => (
  <div className="threshold-section">
    <div className="threshold-inline-container">
      <label className="notification-setting-label threshold-label" htmlFor={id}>
        <FontAwesomeIcon icon={icon} className="setting-label-icon" />
        {label}
      </label>
      <Input
        className="threshold-input-inline"
        id={id}
        max={max}
        min={min}
        onBlur={onBlur}
        onChange={onChange}
        onKeyDown={(event: KeyboardEvent<HTMLInputElement>) => {
          if (event.key === 'Enter') {
            event.currentTarget.blur();
            onEnter?.();
          }
        }}
        placeholder={placeholder}
        step="0.1"
        type="number"
        value={value}
      />
    </div>
  </div>
);

const SwitchRow = ({ id, checked, onChange, label, children }: SwitchRowProps) => (
  <>
    <div className="notification-switch-wrapper">
      <label className="notification-switch" htmlFor={id}>
        <input
          checked={checked}
          className="notification-switch-input"
          id={id}
          onChange={(event) => onChange(event.target.checked)}
          role="switch"
          type="checkbox"
        />
        <span className="notification-switch-track" aria-hidden="true">
          <span className="notification-switch-thumb" />
        </span>
        <span className="notification-setting-label switch-label">{label}</span>
      </label>
    </div>
    {children}
  </>
);

const NotificationGroup = ({ title, children }: NotificationGroupProps) => (
  <section className="notification-setting-group" aria-label={title}>
    <h3 className="notification-setting-group-title">{title}</h3>
    {children}
  </section>
);

const NotificationSettings = () => {
  const { getApiToken, isAuthenticated, authLoading, user } = useUser();
  const apiBaseUrl = process.env.REACT_APP_API_URL || 'api';
  const notificationCacheKey = makeAccountSettingsCacheKey(
    'notifications',
    getAccountSettingsUserCacheKey(user),
    apiBaseUrl,
  );
  const cachedSettings = readAccountSettingsCache<NotificationSettingsState>(notificationCacheKey);

  const [settings, setSettings] = useState(() => cachedSettings || defaultNotificationSettings);
  const [loading, setLoading] = useState(() => !cachedSettings);
  const [loadError, setLoadError] = useState('');
  const [saveError, setSaveError] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const savedSettingsRef = useRef<NotificationSettingsState>(cachedSettings || defaultNotificationSettings);
  const latestSettingsRef = useRef<NotificationSettingsState>(cachedSettings || defaultNotificationSettings);
  const queuedSettingsRef = useRef<NotificationSettingsState | null>(null);
  const failedSettingsRef = useRef<NotificationSettingsState | null>(null);
  const savingRef = useRef(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const getDisplayThreshold = () => {
    const threshold = parseSettingNumber(settings.performanceAlertThreshold);
    return isNaN(threshold) ? '10' : threshold.toString();
  };

  const getDisplayDrawdownThreshold = () => {
    const threshold = parseSettingNumber(settings.drawdownAlertThreshold);
    return isNaN(threshold) ? '25' : threshold.toString();
  };

  const getDisplayKillSwitchThreshold = () => {
    const threshold = parseSettingNumber(settings.killSwitchDrawdownThreshold);
    return isNaN(threshold) ? '40' : threshold.toString();
  };

  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  const loadUserSettings = useCallback(async (options: { force?: boolean } = {}): Promise<void> => {
    const cached = readAccountSettingsCache<NotificationSettingsState>(notificationCacheKey);
    if (cached && !options.force) {
      setSettings(cached);
      savedSettingsRef.current = cached;
      latestSettingsRef.current = cached;
      queuedSettingsRef.current = null;
      failedSettingsRef.current = null;
      setLoadError('');
      setSaveError('');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setLoadError('');
      setSaveError('');

      const token = await getApiToken();

      const loadedSettings = await readThroughAccountSettingsCache<NotificationSettingsState>(notificationCacheKey, async () => {
        const apiUrl = `${apiBaseUrl}/users/notification-settings`;

        const response = await fetch(apiUrl, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (!response.ok) {
          if (response.status === 401) {
            throw new Error('Authentication required. Please log in again.');
          }
          const errorData = await response.json().catch(() => ({})) as NotificationSettingsResponse;
          throw new Error(errorData.message || `Failed to load settings: ${response.status}`);
        }

        const userData = await response.json() as NotificationSettingsResponse;
        const loadedSettings = {
          enablePerformanceAlerts: userData.enablePerformanceAlerts ?? defaultNotificationSettings.enablePerformanceAlerts,
          performanceAlertThreshold: userData.performanceAlertThreshold ?? defaultNotificationSettings.performanceAlertThreshold,
          enableDrawdownAlerts: userData.enableDrawdownAlerts ?? defaultNotificationSettings.enableDrawdownAlerts,
          drawdownAlertThreshold: userData.drawdownAlertThreshold ?? defaultNotificationSettings.drawdownAlertThreshold,
          enableBotStalenessAlerts: userData.enableBotStalenessAlerts ?? defaultNotificationSettings.enableBotStalenessAlerts,
          enableBotErrorAlerts: userData.enableBotErrorAlerts ?? defaultNotificationSettings.enableBotErrorAlerts,
          enableKillSwitch: userData.enableKillSwitch ?? defaultNotificationSettings.enableKillSwitch,
          killSwitchDrawdownThreshold: userData.killSwitchDrawdownThreshold ?? defaultNotificationSettings.killSwitchDrawdownThreshold,
          killSwitchLiquidateAll: userData.killSwitchLiquidateAll ?? defaultNotificationSettings.killSwitchLiquidateAll
        };

        return loadedSettings;
      }, options);

      setSettings(loadedSettings);
      savedSettingsRef.current = loadedSettings;
      latestSettingsRef.current = loadedSettings;
      queuedSettingsRef.current = null;
      failedSettingsRef.current = null;
    } catch (err) {
      const message = getErrorMessage(err, '');
      if (message.includes('login_required')) {
        setLoadError('Please log in again to view your notification settings.');
      } else if (message) {
        setLoadError(message);
      } else {
        setLoadError(`Unable to load notification settings: ${message || 'Please check your connection and try again.'}`);
      }
    } finally {
      setLoading(false);
    }
  }, [apiBaseUrl, getApiToken, notificationCacheKey]);

  useEffect(() => {
    if (isAuthenticated && !authLoading) {
      loadUserSettings();
    } else if (!authLoading && !isAuthenticated) {
      setLoading(false);
      setLoadError('Please log in to view your notification settings.');
    }
  }, [isAuthenticated, authLoading, loadUserSettings]);

  const buildPayload = useCallback((candidate: NotificationSettingsState) => {
    const threshold = parseSettingNumber(candidate.performanceAlertThreshold);
    if (candidate.enablePerformanceAlerts && (isNaN(threshold) || threshold < 1 || threshold > 100)) {
      return { error: 'Performance threshold must be a number between 1 and 100 when alerts are enabled.' };
    }

    const drawdownThreshold = parseSettingNumber(candidate.drawdownAlertThreshold);
    if (candidate.enableDrawdownAlerts && (isNaN(drawdownThreshold) || drawdownThreshold < 5 || drawdownThreshold > 90)) {
      return { error: 'Drawdown threshold must be a number between 5 and 90 when alerts are enabled.' };
    }

    const killSwitchThreshold = parseSettingNumber(candidate.killSwitchDrawdownThreshold);
    if (candidate.enableKillSwitch && (isNaN(killSwitchThreshold) || killSwitchThreshold < 5 || killSwitchThreshold > 90)) {
      return { error: 'Kill switch threshold must be a number between 5 and 90 when enabled.' };
    }

    const savedSettings = savedSettingsRef.current;

    return {
      payload: {
        enablePerformanceAlerts: !!candidate.enablePerformanceAlerts,
        performanceAlertThreshold: isNaN(threshold) ? savedSettings.performanceAlertThreshold : threshold,
        enableBotStalenessAlerts: !!candidate.enableBotStalenessAlerts,
        enableBotErrorAlerts: !!candidate.enableBotErrorAlerts,
        enableDrawdownAlerts: !!candidate.enableDrawdownAlerts,
        drawdownAlertThreshold: isNaN(drawdownThreshold) ? savedSettings.drawdownAlertThreshold : drawdownThreshold,
        enableKillSwitch: !!candidate.enableKillSwitch,
        killSwitchDrawdownThreshold: isNaN(killSwitchThreshold) ? savedSettings.killSwitchDrawdownThreshold : killSwitchThreshold,
        killSwitchLiquidateAll: !!candidate.killSwitchLiquidateAll
      }
    };
  }, []);

  const drainSaveQueue = useCallback(async (): Promise<void> => {
    if (savingRef.current || loading) return;
    const candidate = queuedSettingsRef.current;
    if (!candidate || settingsEqual(candidate, savedSettingsRef.current)) {
      queuedSettingsRef.current = null;
      return;
    }

    const built = buildPayload(candidate);
    if (built.error) {
      queuedSettingsRef.current = null;
      failedSettingsRef.current = null;
      setSaveError(built.error);
      return;
    }
    if (!built.payload) return;
    const payload = built.payload;

    queuedSettingsRef.current = null;
    savingRef.current = true;
    setIsSaving(true);
    setSaveError('');

    try {
      const token = await getApiToken();
      const apiUrl = `${process.env.REACT_APP_API_URL}/users/notification-settings`;

      const response = await fetch(apiUrl, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({})) as NotificationSettingsResponse;
        throw new Error(errorData.message || 'Auto-save failed.');
      }

      const result = await response.json() as NotificationSettingsResponse;
      const savedSettings: NotificationSettingsState = {
        enablePerformanceAlerts: result.enablePerformanceAlerts ?? candidate.enablePerformanceAlerts,
        performanceAlertThreshold: result.performanceAlertThreshold ?? payload.performanceAlertThreshold,
        enableDrawdownAlerts: result.enableDrawdownAlerts ?? candidate.enableDrawdownAlerts,
        drawdownAlertThreshold: result.drawdownAlertThreshold ?? payload.drawdownAlertThreshold,
        enableBotStalenessAlerts: result.enableBotStalenessAlerts ?? candidate.enableBotStalenessAlerts,
        enableBotErrorAlerts: result.enableBotErrorAlerts ?? candidate.enableBotErrorAlerts,
        enableKillSwitch: result.enableKillSwitch ?? candidate.enableKillSwitch,
        killSwitchDrawdownThreshold: result.killSwitchDrawdownThreshold ?? payload.killSwitchDrawdownThreshold,
        killSwitchLiquidateAll: result.killSwitchLiquidateAll ?? candidate.killSwitchLiquidateAll
      };

      savedSettingsRef.current = savedSettings;
      failedSettingsRef.current = null;
      writeAccountSettingsCache(notificationCacheKey, savedSettings);

      if (settingsEqual(latestSettingsRef.current, candidate)) {
        latestSettingsRef.current = savedSettings;
        setSettings(savedSettings);
      } else if (!settingsEqual(latestSettingsRef.current, savedSettings)) {
        queuedSettingsRef.current = latestSettingsRef.current;
      }
    } catch (err) {
      failedSettingsRef.current = candidate;
      setSaveError(getErrorMessage(err, 'Auto-save failed.'));
    } finally {
      savingRef.current = false;
      setIsSaving(false);
      if (queuedSettingsRef.current) {
        void drainSaveQueue();
      }
    }
  }, [buildPayload, getApiToken, loading, notificationCacheKey]);

  const queueSave = useCallback((nextSettings: NotificationSettingsState): void => {
    queuedSettingsRef.current = nextSettings;
    failedSettingsRef.current = null;
    setSaveError('');
    void drainSaveQueue();
  }, [drainSaveQueue]);

  const clearThresholdDebounce = (): void => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }
  };

  const scheduleSave = useCallback((nextSettings: NotificationSettingsState): void => {
    clearThresholdDebounce();
    debounceRef.current = setTimeout(() => {
      debounceRef.current = null;
      queueSave(nextSettings);
    }, AUTOSAVE_DEBOUNCE_MS);
  }, [queueSave]);

  const flushThresholdSave = useCallback((): void => {
    clearThresholdDebounce();
    queueSave(latestSettingsRef.current);
  }, [queueSave]);

  const handleSettingChange = <Key extends NotificationSettingKey>(
    key: Key,
    value: NotificationSettingsState[Key],
    options: { debounce?: boolean } = {},
  ): void => {
    const nextSettings = {
      ...latestSettingsRef.current,
      [key]: value
    };
    latestSettingsRef.current = nextSettings;
    setSettings(nextSettings);
    setSaveError('');

    if (options.debounce) {
      scheduleSave(nextSettings);
      return;
    }

    clearThresholdDebounce();
    queueSave(nextSettings);
  };

  const handleThresholdChange = (e: ChangeEvent<HTMLInputElement>): void => {
    const rawValue = e.target.value;

    if (rawValue === '') {
      handleSettingChange('performanceAlertThreshold', '', { debounce: true });
      return;
    }

    const numericValue = Number(rawValue);
    if (!isNaN(numericValue)) {
      handleSettingChange('performanceAlertThreshold', numericValue, { debounce: true });
    } else {
      handleSettingChange('performanceAlertThreshold', '', { debounce: true });
    }
  };

  const handleDrawdownThresholdChange = (e: ChangeEvent<HTMLInputElement>): void => {
    const rawValue = e.target.value;

    if (rawValue === '') {
      handleSettingChange('drawdownAlertThreshold', '', { debounce: true });
      return;
    }

    const numericValue = Number(rawValue);
    if (!isNaN(numericValue)) {
      handleSettingChange('drawdownAlertThreshold', numericValue, { debounce: true });
    } else {
      handleSettingChange('drawdownAlertThreshold', '', { debounce: true });
    }
  };

  const retryFailedSave = (): void => {
    queueSave(failedSettingsRef.current || latestSettingsRef.current);
  };

  const isInitialLoading = loading || authLoading;

  return (
    <div className="notification-management">
      <div className="settings-subpage-header notification-header">
        <div className="settings-subpage-header-left">
          <h2 className="settings-subpage-title">Notification Preferences</h2>
          <p className="settings-subpage-description">
            Control which strategy and deployment events BotSpot should alert you about.
          </p>
        </div>
      </div>

      {isInitialLoading ? (
        <AccountSettingsLoadingState
          className="notification-management-loading"
          label="Loading notification settings"
          text="Loading notification settings..."
          variant="section"
        />
      ) : loadError ? (
        <Alert variant="danger" className="notification-management-error">
          <FontAwesomeIcon icon={faExclamationTriangle} className="notification-alert-icon" />
          <span>{loadError}</span>
          <Button
            className="notification-retry-button"
            onClick={() => loadUserSettings({ force: true })}
            size="sm"
            variant="danger"
          >
            Retry
          </Button>
        </Alert>
      ) : (
        <>
          {saveError && (
        <Alert variant="danger" className="notification-management-error notification-save-error">
          <FontAwesomeIcon icon={faExclamationTriangle} className="notification-alert-icon" />
          <span>{saveError}</span>
          {failedSettingsRef.current && (
            <Button
              className="notification-retry-button"
              disabled={isSaving}
              onClick={retryFailedSave}
              size="sm"
              variant="danger"
            >
              Retry
            </Button>
          )}
        </Alert>
          )}

      <NotificationGroup title="Performance Alerts">
        <div className="notification-option">
          <SwitchRow
            checked={settings.enablePerformanceAlerts}
            id="performance-alerts-switch"
            label="Enable performance alerts"
            onChange={(checked) => handleSettingChange('enablePerformanceAlerts', checked)}
          >
            <small className="text-muted notification-option-help">
              Get notified when strategy 24-hour performance changes by <strong>{getDisplayThreshold()}%</strong> or more
            </small>
          </SwitchRow>

          {settings.enablePerformanceAlerts && (
            <NumberField
              icon={faInfoCircle}
              id="performance-alert-threshold"
              label="Performance Threshold"
              max="100"
              min="1"
              onBlur={flushThresholdSave}
              onChange={handleThresholdChange}
              onEnter={flushThresholdSave}
              placeholder="10.0"
              value={settings.performanceAlertThreshold}
            />
          )}
        </div>
      </NotificationGroup>

      <NotificationGroup title="Drawdown Alerts">
        <div className="notification-option">
          <SwitchRow
            checked={settings.enableDrawdownAlerts}
            id="drawdown-alerts-switch"
            label="Enable drawdown alerts"
            onChange={(checked) => handleSettingChange('enableDrawdownAlerts', checked)}
          >
            <small className="text-muted notification-option-help">
              Get notified when strategy portfolio value drops by <strong>{getDisplayDrawdownThreshold()}%</strong> or more
            </small>
          </SwitchRow>

          {settings.enableDrawdownAlerts && (
            <NumberField
              icon={faInfoCircle}
              id="drawdown-alert-threshold"
              label="Drawdown Threshold"
              max="90"
              min="5"
              onBlur={flushThresholdSave}
              onChange={handleDrawdownThresholdChange}
              onEnter={flushThresholdSave}
              placeholder="25.0"
              value={settings.drawdownAlertThreshold}
            />
          )}
        </div>
      </NotificationGroup>

      <NotificationGroup title="Strategy Staleness Alerts">
        <div className="notification-option">
          <SwitchRow
            checked={settings.enableBotStalenessAlerts}
            id="staleness-alerts-switch"
            label="Enable strategy staleness alerts"
            onChange={(checked) => handleSettingChange('enableBotStalenessAlerts', checked)}
          >
            <small className="text-muted notification-option-help">
              Get notified when a strategy has not been active for an extended period
            </small>
          </SwitchRow>
        </div>
      </NotificationGroup>

      <NotificationGroup title="Strategy Error Alerts">
        <div className="notification-option">
          <SwitchRow
            checked={settings.enableBotErrorAlerts}
            id="error-alerts-switch"
            label="Enable strategy error alerts"
            onChange={(checked) => handleSettingChange('enableBotErrorAlerts', checked)}
          >
            <small className="text-muted notification-option-help">
              Get notified when a strategy encounters errors or issues
            </small>
          </SwitchRow>
        </div>
      </NotificationGroup>

      <NotificationGroup title="Kill Switch">
        <div className="notification-option">
          <SwitchRow
            checked={settings.enableKillSwitch}
            id="kill-switch-toggle"
            label={(
              <>
                Automatically stop my live deployments when drawdown exceeds <strong>{getDisplayKillSwitchThreshold()}%</strong>
              </>
            )}
            onChange={(checked) => handleSettingChange('enableKillSwitch', checked)}
          >
            <small className="text-muted notification-option-help">
              Applies only to live (real money) deployments. When triggered, the bot is stopped and must be restarted
              manually. Existing broker positions are not sold unless you also enable the sell-everything option below.
            </small>
          </SwitchRow>

          {settings.enableKillSwitch && (
            <div className="threshold-section">
              <NumberField
                icon={faInfoCircle}
                id="kill-switch-drawdown-threshold"
                label="Kill Switch Drawdown Threshold"
                max="90"
                min="5"
                onChange={(e) => {
                  const rawValue = e.target.value;
                  if (rawValue === '') {
                    handleSettingChange('killSwitchDrawdownThreshold', '', { debounce: true });
                    return;
                  }
                  const numericValue = parseFloat(rawValue);
                  if (!isNaN(numericValue)) {
                    handleSettingChange('killSwitchDrawdownThreshold', numericValue, { debounce: true });
                  } else {
                    handleSettingChange('killSwitchDrawdownThreshold', '', { debounce: true });
                  }
                }}
                onBlur={flushThresholdSave}
                onEnter={flushThresholdSave}
                placeholder="40.0"
                value={settings.killSwitchDrawdownThreshold}
              />

              <div className="liquidate-all-option">
                <label className="liquidate-checkbox" htmlFor="kill-switch-liquidate-all">
                  <input
                    checked={settings.killSwitchLiquidateAll}
                    id="kill-switch-liquidate-all"
                    onChange={(event) => handleSettingChange('killSwitchLiquidateAll', event.target.checked)}
                    type="checkbox"
                  />
                  <span>Also sell everything in my account when we hit this</span>
                </label>

                {settings.killSwitchLiquidateAll && (
                  <div className="liquidate-warning">
                    <FontAwesomeIcon icon={faExclamationTriangle} className="warning-icon" />
                    <span>
                      <strong>WARNING:</strong> This will sell everything in your account, even outside of this strategy.
                      When you do this, it also will sell everything in your account, including things that the strategy never touched before.
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </NotificationGroup>
        </>
      )}
    </div>
  );
};

export default NotificationSettings;
